import { FC, useState } from "react"
import { ChevronDown, X, Calendar, CalendarDays } from "lucide-react"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import "./date-filter.css"
import { DateTime } from "luxon"

export type DateRangePreset =
  | "all"
  | "today"
  | "last_7"
  | "last_30"
  | "last_90"
  | "custom"

interface DateFilterProps {
  selectedRange: DateRangePreset
  customStartDate: Date | null
  customEndDate: Date | null
  onRangeChange: (
    range: DateRangePreset,
    startDate?: Date,
    endDate?: Date
  ) => void
  onClearFilter?: () => void
  hideLabelOnMobile?: boolean
}

interface PresetOption {
  value: DateRangePreset
  label: string
  description: string
}

const PRESET_OPTIONS: PresetOption[] = [
  { value: "all", label: "All Time", description: "Show all results" },
  { value: "today", label: "Today", description: "Results from today" },
  {
    value: "last_7",
    label: "Last 7 Days",
    description: "Past week of results"
  },
  {
    value: "last_30",
    label: "Last 30 Days",
    description: "Past month of results"
  },
  {
    value: "last_90",
    label: "Last 90 Days",
    description: "Past quarter of results"
  },
  { value: "custom", label: "Custom Range", description: "Pick specific dates" }
]

const getPresetLabel = (
  preset: DateRangePreset,
  startDate?: Date | null,
  endDate?: Date | null
): string => {
  if (preset === "custom" && startDate && endDate) {
    const start = DateTime.fromJSDate(startDate).toFormat("MMM dd")
    const end = DateTime.fromJSDate(endDate).toFormat("MMM dd, yyyy")
    return `${start} - ${end}`
  }
  return PRESET_OPTIONS.find(p => p.value === preset)?.label || "All Time"
}

const DateFilter: FC<DateFilterProps> = ({
  selectedRange,
  customStartDate,
  customEndDate,
  onRangeChange,
  onClearFilter,
  hideLabelOnMobile = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [tempStartDate, setTempStartDate] = useState<Date | null>(
    customStartDate
  )
  const [tempEndDate, setTempEndDate] = useState<Date | null>(customEndDate)
  const [showingCustomPicker, setShowingCustomPicker] = useState(false)

  const isActive = selectedRange !== "all"
  const displayLabel = getPresetLabel(
    selectedRange,
    customStartDate,
    customEndDate
  )

  const handlePresetSelect = (preset: DateRangePreset) => {
    if (preset === "custom") {
      setShowingCustomPicker(true)
      setTempStartDate(customStartDate)
      setTempEndDate(customEndDate)
    } else {
      setShowingCustomPicker(false)
      onRangeChange(preset)
      setIsOpen(false)
    }
  }

  const handleApplyCustomRange = () => {
    if (tempStartDate && tempEndDate) {
      onRangeChange("custom", tempStartDate, tempEndDate)
      setShowingCustomPicker(false)
      setIsOpen(false)
    }
  }

  const handleCancelCustomRange = () => {
    setShowingCustomPicker(false)
    setTempStartDate(customStartDate)
    setTempEndDate(customEndDate)
  }

  return (
    <div className="relative">
      {/* Filter Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-200 ${
          isActive
            ? "bg-primary-50 border-primary-300 text-primary-700 hover:bg-primary-100"
            : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
        }`}
      >
        <Calendar className="w-4 h-4" />
        <span
          className={`text-sm font-medium ${
            hideLabelOnMobile ? "hidden md:block" : ""
          }`}
        >
          {displayLabel}
        </span>
        <ChevronDown
          className={`w-4 h-4 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Clear Button */}
      {isActive && onClearFilter && (
        <button
          onClick={onClearFilter}
          className="ml-2 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 text-sm transition-colors duration-200"
        >
          <X className="w-3.5 h-3.5" />
          Clear
        </button>
      )}

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => {
              setIsOpen(false)
              setShowingCustomPicker(false)
            }}
          />

          {/* Menu */}
          <div
            className={`absolute right-0 z-20 mt-2 w-72 bg-white rounded-xl shadow-lg border border-slate-200 animate-[fadeSlideIn_0.2s_ease-out_forwards] ${
              showingCustomPicker ? "" : "overflow-hidden"
            }`}
          >
            {!showingCustomPicker ? (
              <>
                <div className="p-4 border-b border-slate-200 bg-slate-50">
                  <h3 className="text-sm font-semibold text-slate-900">
                    Filter by Date Range
                  </h3>
                  <p className="text-xs text-slate-600 mt-1">
                    Choose a time period
                  </p>
                </div>

                {/* Preset Options */}
                <div className="p-2 overflow-hidden">
                  {PRESET_OPTIONS.map(option => (
                    <button
                      key={option.value}
                      onClick={() => handlePresetSelect(option.value)}
                      className={`w-full flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors duration-150 text-left ${
                        selectedRange === option.value ? "bg-primary-50" : ""
                      }`}
                    >
                      <div className="flex-1">
                        <div
                          className={`text-sm font-medium ${
                            selectedRange === option.value
                              ? "text-primary-700"
                              : "text-slate-900"
                          }`}
                        >
                          {option.label}
                        </div>
                        <div className="text-xs text-slate-600 mt-0.5">
                          {option.description}
                        </div>
                      </div>
                      {selectedRange === option.value && (
                        <div className="w-2 h-2 rounded-full bg-primary-600 mt-1.5" />
                      )}
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="p-4 border-b border-slate-200 bg-slate-50">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-slate-600" />
                    <h3 className="text-sm font-semibold text-slate-900">
                      Custom Date Range
                    </h3>
                  </div>
                </div>

                {/* Custom Date Pickers */}
                <div className="p-4 space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2">
                      Start Date
                    </label>
                    <DatePicker
                      selected={tempStartDate}
                      onChange={date => setTempStartDate(date)}
                      selectsStart
                      startDate={tempStartDate}
                      endDate={tempEndDate}
                      maxDate={tempEndDate || new Date()}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      dateFormat="MMM dd, yyyy"
                      placeholderText="Select start date"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-2">
                      End Date
                    </label>
                    <DatePicker
                      selected={tempEndDate}
                      onChange={date => setTempEndDate(date)}
                      selectsEnd
                      startDate={tempStartDate}
                      endDate={tempEndDate}
                      minDate={tempStartDate}
                      maxDate={new Date()}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      dateFormat="MMM dd, yyyy"
                      placeholderText="Select end date"
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={handleCancelCustomRange}
                      className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 transition-colors duration-150"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleApplyCustomRange}
                      disabled={!tempStartDate || !tempEndDate}
                      className="flex-1 px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default DateFilter
